import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import isEmpty from 'lodash-es/isEmpty';

import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import Popup from '@ecp/common/src/components/modal/Popup';

import { getSiteSubsidiary } from '@bo-utils/siteUtils';
import { useAlertDialog } from '@bo-hooks/common/useModal';
import boSiteApi from '@bo-apis/system/siteApi';
import coSiteApi from '@bo-apis/co/system/coSiteApi';
import Autocomplete from '@bo-components/common/autocomplete/Autocomplete';
import SubsidiarySearchTree from '@bo-components/common/search/SubsidiarySearchTree';
import { isCODomain } from '@bo-const/common/domain';

const Container = styled(FlexBoxColumn)`
  flex-grow: 1;
`;

const SubsidiaryAutocomplete = ({
  onChange,
  onSelect,
  type,
  siteNumber,
  subsidiaryCompanyCode,
  disabled = false,
  readOnly = false,
  filterByJobGroup,
  error,
  errorMessage,
}) => {
  const isCo = isCODomain();
  const siteApi = isCo ? coSiteApi : boSiteApi;

  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedItemLabel, setSelectedItemLabel] = useState('');
  const [subsidiaryList, setSubsidiaryList] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [defaultKeyword, setDefaultKeyword] = useState('');

  const [siteList, setSiteList] = useState([]);
  const [siteSubsidiaryList, setSiteSubsidiaryList] = useState([]);

  const isSite = useMemo(() => type !== 'subsidiary', [type]);
  const popupTitle = useMemo(() => (isSite ? '사이트 조회' : '사이트/계열사 조회'), [isSite]);
  const lastSearchKeyword = useRef('');

  const { showAlertDialog } = useAlertDialog();

  useEffect(() => {
    (async () => {
      if (filterByJobGroup) {
        const { result: siteList = [] } = await siteApi.getSiteMgmtList({ skipSpinner: true });
        const { result: subsidiaryList = [] } = await siteApi.getSubsidiaryCompanyList({ skipSpinner: true });
        setSiteList(siteList.map((site) => ({ ...site, itemLabel: site.siteName })));
        setSiteSubsidiaryList(getSiteSubsidiary(subsidiaryList));
      } else {
        const { result: allSiteSimple = [] } = await siteApi.getAllSiteSimpleList({ skipSpinner: true });
        const { result: allSiteSubsidiary = [] } = await siteApi.getAllSubsidiaryCompanyList({ skipSpinner: true });
        setSiteList(allSiteSimple.map((site) => ({ ...site, itemLabel: site.siteName })));
        setSiteSubsidiaryList(getSiteSubsidiary(allSiteSubsidiary));
      }
    })();
  }, [filterByJobGroup]);

  useEffect(() => {
    (async () => {
      const refinedList = isSite ? siteList : siteSubsidiaryList;
      setSubsidiaryList(refinedList);
    })();
  }, [isSite, siteList, siteSubsidiaryList]);

  useEffect(() => {
    const defaultLabel =
      subsidiaryList.find(
        isSite
          ? (site) => site.siteNumber === siteNumber
          : (subsidiary) =>
              subsidiary.siteNumber === siteNumber && subsidiary.subsidiaryCompanyCode === subsidiaryCompanyCode
      )?.itemLabel || '';

    setSelectedItemLabel(defaultLabel);
    setSearchKeyword(defaultLabel);
  }, [setSelectedItemLabel, setSearchKeyword, siteNumber, subsidiaryCompanyCode, isSite, subsidiaryList]);

  const handleSearchKeywordChange = useCallback(
    (event) => {
      setSearchKeyword(event?.target?.value);
      lastSearchKeyword.current = event?.target?.value;
    },
    [setSearchKeyword]
  );

  const handleAutocompleteSearchKeywordChange = useCallback(
    (event) => {
      onChange?.();
      handleSearchKeywordChange(event);
      if (event?.target?.value?.length === 0) {
        onSelect?.({});
        setSelectedItemLabel('');
      }
    },
    [onChange, handleSearchKeywordChange, onSelect]
  );

  const setCachedSearchKeyword = useCallback(
    (value) => {
      setSearchKeyword(value);
      lastSearchKeyword.current = value;
    },
    [setSearchKeyword]
  );

  const handleSubsidiaryClick = useCallback(
    (name, item) => {
      const { siteNumber, siteName, subsidiaryCompanyCode, subsidiaryCompanyName, itemLabel } = item;
      const duplicated = onSelect?.({ siteNumber, siteName, subsidiaryCompanyCode, subsidiaryCompanyName }) ?? false;
      if (!duplicated) {
        setSelectedItemLabel(itemLabel);
        setCachedSearchKeyword(itemLabel);
      }
    },
    [onSelect, setCachedSearchKeyword]
  );

  const restoreSearchKeyword = useCallback(() => {
    setSearchKeyword(selectedItemLabel);
    lastSearchKeyword.current = selectedItemLabel;
  }, [selectedItemLabel]);

  const handleBlur = useCallback(() => {
    restoreSearchKeyword();
  }, [restoreSearchKeyword]);

  const handleSearchClick = useCallback(() => {
    setDefaultKeyword(searchKeyword);
    setShowPopup(true);
    restoreSearchKeyword();
  }, [restoreSearchKeyword, searchKeyword]);

  const popupSelectItem = useRef({});
  const handleTreeSelect = useCallback(
    (key) => {
      popupSelectItem.current = subsidiaryList.find(
        isSite
          ? ({ siteNumber }) => siteNumber === key
          : ({ siteNumber, subsidiaryCompanyCode }) => `${siteNumber}-${subsidiaryCompanyCode}` === key
      );
    },
    [subsidiaryList, isSite]
  );

  const handlePopupConfirm = useCallback(() => {
    if (isEmpty(popupSelectItem.current)) {
      showAlertDialog('등록할 사이트/계열사를 선택해주세요.');
      return;
    }
    if (Object?.keys(popupSelectItem.current)?.length > 0) {
      const { siteNumber, siteName, subsidiaryCompanyCode, subsidiaryCompanyName, itemLabel } = popupSelectItem.current;
      const duplicated = onSelect?.({ siteNumber, siteName, subsidiaryCompanyCode, subsidiaryCompanyName }) ?? false;
      if (!duplicated) {
        setSelectedItemLabel(itemLabel);
        setCachedSearchKeyword(itemLabel);
      }
    }

    popupSelectItem.current = {};
    setShowPopup(false);
  }, [onSelect, setCachedSearchKeyword, showAlertDialog]);

  const handlePopupCancel = useCallback(() => {
    setShowPopup(false);
    setDefaultKeyword('');
    popupSelectItem.current = {};
  }, [setShowPopup]);

  return (
    <Container onBlur={handleBlur}>
      <Autocomplete
        placeholder={'입력하세요.'}
        onChange={handleAutocompleteSearchKeywordChange}
        value={searchKeyword}
        setValue={setCachedSearchKeyword}
        items={subsidiaryList}
        handleItemClick={handleSubsidiaryClick}
        handleSearchClick={handleSearchClick}
        width={'100%'}
        height={'100%'}
        maxLength={100}
        disabled={disabled}
        readOnly={readOnly}
        error={error}
        errorMessage={errorMessage}
      />
      <Popup
        type={'Medium'}
        open={showPopup}
        header={popupTitle}
        onClose={handlePopupCancel}
        onCancelClick={handlePopupCancel}
        onConfirmClick={handlePopupConfirm}
      >
        <SubsidiarySearchTree
          type={type}
          subsidiaryList={subsidiaryList}
          onSelect={handleTreeSelect}
          searchKeyword={lastSearchKeyword.current}
          defaultKeyword={defaultKeyword}
        />
      </Popup>
    </Container>
  );
};

SubsidiaryAutocomplete.propTypes = {
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  type: PropTypes.oneOf(['site', 'subsidiary']),
  siteNumber: PropTypes.string,
  subsidiaryCompanyCode: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  filterByJobGroup: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
};

SubsidiaryAutocomplete.defaultProps = {
  type: 'subsidiary',
  filterByJobGroup: true,
};

export default SubsidiaryAutocomplete;